<template>
  <img alt="Renesas logo" :src="logo" />
  <SessionLauncher
    :serviceUrl="serviceUrl"
    :appDefinition="resolvedAppDefinition"
    :email="email"
    :appId="appId"
    :useEphemeralStorage="useEphemeralStorage"
    :workspaceName="workspaceName"
    :token="token"
  />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import SessionLauncher from './components/SessionLauncher.vue';
import Keycloak, { KeycloakConfig } from 'keycloak-js';
import { v4 as uuidv4 } from 'uuid';

interface AppData {
  email: string | undefined;
  token: string | undefined;
  resolvedAppDefinition: string | undefined;
  workspaceName?: string;
  logo: string;
}

export default defineComponent({
  name: 'App',
  props: {
    appId: String,
    appName: String,
    useKeycloak: Boolean,
    keycloakAuthUrl: String,
    keycloakRealm: String,
    keycloakClientId: String,
    serviceUrl: String,
    appDefinition: String,
    useEphemeralStorage: Boolean,
    vipUsers: String
  },
  components: {
    SessionLauncher
  },
  data() {
    return {
      email: undefined,
      token: undefined,
      resolvedAppDefinition: this.appDefinition,
      workspaceName: undefined,
      // https://cli.vuejs.org/guide/html-and-static-assets.html#the-public-folder
      logo: process.env.BASE_URL + 'logo.png'
    } as AppData;
  },
  created() {
    document.title = this.appName ?? 'Renesas';
    if (this.useKeycloak) {
      const keycloakConfig: KeycloakConfig = {
        url: this.keycloakAuthUrl,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        realm: this.keycloakRealm!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        clientId: this.keycloakClientId!
      };

      const keycloak = new Keycloak(keycloakConfig);

      keycloak
        .init({
          onLoad: 'check-sso',
          redirectUri: window.location.href,
          checkLoginIframe: false
        })
        .then(auth => {
          if (!auth) {
            keycloak.login({
              redirectUri: window.location.href,
              idpHint: 'myrenesas-oidc'
            });
          } else {
            const parsedToken = keycloak.idTokenParsed;
            if (parsedToken) {
              const userMail = parsedToken.email;
              this.workspaceName = this.useEphemeralStorage
                ? undefined
                : 'ws-' + this.appId + '-' + this.appDefinition + '-' + userMail;
              this.token = keycloak.idToken;
              const userId = keycloak.subject;
              this.resolvedAppDefinition = isVip(this.vipUsers, userId)
                ? `${this.appDefinition}-vip`
                : this.appDefinition;
              this.email = userMail;
            }
          }
        })
        .catch(() => {
          console.error('Authentication Failed');
        });
    } else {
      // for ephemeral storage we generate new addresses every time, for fixed storages we simulate a stable user with a fixed address
      const userMail = (this.useEphemeralStorage ? uuidv4() : this.appId + '-' + this.appDefinition) + '@theia.cloud';
      this.workspaceName = this.useEphemeralStorage
        ? undefined
        : 'ws-' + this.appId + '-' + this.appDefinition + '-' + userMail;
      this.email = userMail;
    }
  }
});

const isVip = (vipUsers: string | undefined, userId: string | undefined): boolean => {
  if (vipUsers && userId) {
    const vips = vipUsers.trim().split(',');
    return vips.includes(userId);
  }
  return false;
};
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #cccccc;
  background-color: rgb(30, 30, 30);
  margin-top: 60px;
}
</style>
